// Etsy Profit Calculator strings — EN + TR.
//
// Wave-5 Day 1. EtsyProfitCalculator (`features/shared/EtsyProfitCalculator.tsx`)
// chrome localization. Sarah BO2 US Reseller persona ana hedef (audit:
// "%95 pure TR Sarah broken"). Shared admin + seller surface; same component
// mounts under both /admin/tools/etsy-profit and /seller/tools/etsy-profit.
//
// AUDIT LARGEST TOOL DEEP-DIVE ~95 string:
// - title + role-aware subtitle
// - 30+ field labels + placeholders
// - 8 BREAKDOWN_LABELS (Etsy fees decomposition)
// - 4 result cards (Total Revenue / Total Etsy Fees / Operating Cost / Net Profit)
// - warnings (rate warning + monthly section header)
// - country + currency picker + monthly projection section
//
// 🔴 SELLER-LEAK INVARIANT: result card subtitles persona-neutral
// platform-perspective (e.g. "Operating cost" / "Operasyon gideri" —
// NOT vendor name). "Production" / "Üretim" tokens in this dict are
// generic cost labels not vendor references; refined seller-leak pattern
// (Mehmet/Hakan/Aybars/Furkan/Workshop/Bursa/Supplier) allows them.

import type { LocalizedDict } from '../index'

type EtsyProfitStrings = {
  pageTitle: string
  subtitle: {
    common: string
    sellerSuffix: string
    adminSuffix: string
  }
  inputSections: {
    country: {
      title: string
      countryLabel: string
      countryOptions: { us: string; tr: string }
      usdToTryLabel: string
    }
    revenue: {
      title: string
      itemPriceLabel: string
      quantityLabel: string
      shippingChargedLabel: string
      giftWrapChargedLabel: string
    }
    costs: {
      title: string
      unitCostLabel: string
      shippingCostLabel: string
      packagingCostLabel: string
      adCostPerOrderLabel: string
    }
    etsySettings: {
      title: string
      offsiteAdsLabel: string
      offsiteAdsOptions: { none: string; twelve: string; fifteen: string }
      sellerFeeTaxLabel: string
      currencyConversionLabel: string
      depositFeeLabel: string
    }
  }
  warnings: {
    rateWarningPrefix: string
    rateWarningSuffix: string
    rateWarningKurLabel: string
  }
  resultCards: {
    grossRevenue: string
    totalEtsyFees: string
    operatingCost: string
    netProfit: string
    marginBadge: (percent: string) => string
  }
  breakdownLabels: {
    sectionTitle: string
    listingFee: string
    transaction: string
    payment: string
    regulatory: string
    offsiteAds: string
    currencyConv: string
    depositFee: string
    tax: string
    totalRow: string
  }
  monthly: {
    sectionTitle: string
    perDaySuffix: string
  }
}

export const ETSY_PROFIT_STRINGS: LocalizedDict<EtsyProfitStrings> = {
  en: {
    pageTitle: 'Etsy Profit Calculator',
    subtitle: {
      common:
        "Subtract Etsy fees from your own costs, see net profit and margin · USD-based",
      sellerSuffix: 'For sale price decisions',
      adminSuffix: 'Admin view',
    },
    inputSections: {
      country: {
        title: 'Country & Rate',
        countryLabel: 'Country profile',
        countryOptions: { us: 'USA (US)', tr: 'Turkey (TR)' },
        usdToTryLabel: 'USD → TRY rate',
      },
      revenue: {
        title: 'Sale (revenue base)',
        itemPriceLabel: 'Item price',
        quantityLabel: 'Quantity',
        shippingChargedLabel: 'Shipping charged to customer',
        giftWrapChargedLabel: 'Gift wrap (charged)',
      },
      costs: {
        title: 'Your costs',
        unitCostLabel: 'Item cost (unit)',
        shippingCostLabel: 'Shipping cost',
        packagingCostLabel: 'Packaging cost',
        adCostPerOrderLabel: 'Ad cost / order',
      },
      etsySettings: {
        title: 'Etsy settings',
        offsiteAdsLabel: 'Offsite Ads',
        offsiteAdsOptions: { none: 'None (0%)', twelve: '12%', fifteen: '15%' },
        sellerFeeTaxLabel: 'Tax (on seller fee)',
        currencyConversionLabel: 'Currency conversion fee (2.5%)',
        depositFeeLabel: 'Apply deposit fee (₺42)',
      },
    },
    warnings: {
      rateWarningPrefix:
        "TR fixed fees (₺14 + ₺42) couldn't be converted to USD — enter a valid",
      rateWarningSuffix: '. These fees are currently not included.',
      rateWarningKurLabel: 'USD → TRY rate',
    },
    resultCards: {
      grossRevenue: 'Gross Revenue',
      totalEtsyFees: 'Total Etsy Fees',
      operatingCost: 'Operating Cost',
      netProfit: 'Net Profit',
      marginBadge: (percent) => `Margin ${percent}%`,
    },
    breakdownLabels: {
      sectionTitle: 'Etsy fee breakdown',
      listingFee: 'Listing fee',
      transaction: 'Transaction fee (6.5%)',
      payment: 'Payment processing fee',
      regulatory: 'Regulatory fee (TR 2.27%)',
      offsiteAds: 'Offsite Ads',
      currencyConv: 'Currency conversion (2.5%)',
      depositFee: 'Deposit fee (TR)',
      tax: 'VAT (on seller fee)',
      totalRow: 'Total',
    },
    monthly: {
      sectionTitle: 'Monthly projection (net profit × sales/day × 30)',
      perDaySuffix: '/day',
    },
  },
  tr: {
    pageTitle: 'Etsy Kâr Hesaplayıcı',
    subtitle: {
      common:
        'Kendi maliyetlerinle Etsy ücretlerini düş, net kârını ve marjını gör · USD bazlı',
      sellerSuffix: 'Satış fiyatı kararı için',
      adminSuffix: 'Admin görünümü',
    },
    inputSections: {
      country: {
        title: 'Ülke & Kur',
        countryLabel: 'Ülke profili',
        countryOptions: { us: 'ABD (US)', tr: 'Türkiye (TR)' },
        usdToTryLabel: 'USD → TRY kuru',
      },
      revenue: {
        title: 'Satış (gelir tabanı)',
        itemPriceLabel: 'Ürün fiyatı',
        quantityLabel: 'Adet',
        shippingChargedLabel: 'Müşteriden alınan kargo',
        giftWrapChargedLabel: 'Hediye paketi (alınan)',
      },
      costs: {
        title: 'Maliyetlerin',
        unitCostLabel: 'Ürün maliyeti (birim)',
        shippingCostLabel: 'Kargo maliyeti',
        packagingCostLabel: 'Paketleme maliyeti',
        adCostPerOrderLabel: 'Reklam / sipariş',
      },
      etsySettings: {
        title: 'Etsy ayarları',
        offsiteAdsLabel: 'Offsite Ads',
        offsiteAdsOptions: { none: 'Yok (%0)', twelve: '%12', fifteen: '%15' },
        sellerFeeTaxLabel: 'KDV (satıcı ücreti)',
        currencyConversionLabel: 'Döviz çevrim ücreti (%2.5)',
        depositFeeLabel: 'Mevduat ücreti uygula (42 ₺)',
      },
    },
    warnings: {
      rateWarningPrefix:
        "TR sabit ücretleri (14 ₺ + 42 ₺) USD'ye çevrilemedi — geçerli bir",
      rateWarningSuffix: ' gir. Bu ücretler şu an hesaba katılmıyor.',
      rateWarningKurLabel: 'USD → TRY kuru',
    },
    resultCards: {
      grossRevenue: 'Toplam Gelir',
      totalEtsyFees: 'Toplam Etsy Kesintisi',
      operatingCost: 'Operasyon Gideri',
      netProfit: 'Net Kâr',
      marginBadge: (percent) => `Marj ${percent}%`,
    },
    breakdownLabels: {
      sectionTitle: 'Etsy kesinti detayları',
      listingFee: 'Liste ücreti',
      transaction: 'İşlem ücreti (%6.5)',
      payment: 'Ödeme işlem ücreti',
      regulatory: 'Düzenleyici ücret (TR %2.27)',
      offsiteAds: 'Offsite Ads',
      currencyConv: 'Döviz çevrim (%2.5)',
      depositFee: 'Mevduat ücreti (TR)',
      tax: 'KDV (satıcı ücreti üzerine)',
      totalRow: 'Toplam',
    },
    monthly: {
      sectionTitle: 'Aylık projeksiyon (net kâr × satış/gün × 30)',
      perDaySuffix: '/gün',
    },
  },
}
